import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../config';

const TetraConnect: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchTetraData = async () => {
      try {
        if (!token) {
          throw new Error('No Tetra session token provided');
        }
        
        console.log("Fetching Tetra data with token:", token);
        
        // Store the token for after login
        sessionStorage.setItem('tetraConnectToken', token);
        
        // Check if user is authenticated
        const authToken = localStorage.getItem('jwtToken');
        if (!authToken) {
          console.log("No auth token found, redirecting to login");
          sessionStorage.setItem('redirectAfterLogin', `/tetra-connect/${token}`);
          navigate('/login');
          return;
        }
        
        // Fetch the Tetra session data with authentication
        const response = await fetch(`${config.reactServer}api/v1/get-tetra-session/${token}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          credentials: 'include'  // Important for cookies
        });
        
        console.log("Tetra data response status:", response.status);
        
        if (!response.ok) {
          if (response.status === 401) {
            console.log('Auth token expired, redirecting to login');
            sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
            navigate('/login');
            return;
          }
          
          const errorData = await response.json();
          setError(errorData.error || 'Failed to retrieve Tetra data');
          setLoading(false);
          return;
        }
        
        const data = await response.json();
        console.log("Tetra data received:", data);
        
        // Store in sessionStorage
        sessionStorage.setItem('tetraData', JSON.stringify(data));
        console.log("Tetra data stored in sessionStorage");
        
        // Redirect to main app
        navigate('/');
      } catch (err: any) {
        console.error('Error retrieving Tetra session:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTetraData();
  }, [token, navigate]);
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-xl mb-4">Verbinding maken met Tetra...</div>
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-xl text-red-600 mb-4">Fout bij verbinden met Tetra</div>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
        <button 
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={() => navigate('/')}
        >
          Ga naar dashboard
        </button>
      </div>
    );
  }
  
  // This component should never reach this point as it always redirects or shows loading/error
  return null;
};

export default TetraConnect;